import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { ArrowLeft, Loader2, Clock, MapPin, Upload, Download, FileCheck, CheckCircle2, XCircle, Plus, Trash2, ChevronRight, Receipt, Store } from 'lucide-react';
import { toast } from 'sonner';
import MapView, { geocodeAddress } from '@/components/MapView';
import PositionPicker from '@/components/PositionPicker';
import BestOptionFinder from '@/components/BestOptionFinder';
import {
  CATEGORY_LIST, REPAIR_CATEGORIES, getSubtypes, getCategoryLabel, formatSubtype,
  requiresPosition, getCloseoutIssues
} from '@/constants/repairOptions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { STATUS_COLORS, REPAIR_STATUSES, STATUS_LABELS, REPAIR_PIPELINE_STAGES, formatStatus } from '@/constants/statusColors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = REPAIR_STATUSES;
const CATS = CATEGORY_LIST.map(c => c.value);
const PRIORITY_COLORS = { emergency:'text-red-400',high:'text-orange-400',medium:'text-[#3B82F6]',low:'text-[#8B9AAB]' };
const PAYMENT_COLORS = { paid:'text-[#57EB31]',unpaid:'text-[#3B82F6]',overdue:'text-red-400' };

// New line item empty state
const EMPTY_ITEM = { category: '', subtype: '', position_code: 'NONE', description: '', qty: '', amount: '' };

export default function RepairDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [repair, setRepair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [geocoding, setGeocoding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [invoiceNum, setInvoiceNum] = useState('');
  const [actualCost, setActualCost] = useState('');
  const fileInputRef = useRef(null);
  const [showNextStep, setShowNextStep] = useState(false);
  const [routingToGenerator, setRoutingToGenerator] = useState(false);

  // Line items
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState(EMPTY_ITEM);
  const [addingItem, setAddingItem] = useState(false);

  // Linked invoices
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [shopLocatingPrompt, setShopLocatingPrompt] = useState(false);
  const [shopLocatingVendorId, setShopLocatingVendorId] = useState('');
  const [shopLocatingVendorName, setShopLocatingVendorName] = useState('');

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    // Load vendor list once for the assignment dropdown
    api.get('/vendors', { params: { limit: 500 } })
      .then(r => setVendors(r.data.items || r.data || []))
      .catch(() => setVendors([]));
  }, []);

  const handleGeocode = async () => {
    if (!repair?.location) return;
    setGeocoding(true);
    try {
      const coords = await geocodeAddress(repair.location);
      if (coords) {
        await api.put(`/repairs/${id}`, { lat: coords.lat, lng: coords.lng });
        load();
        toast.success('Location pinned on map!');
      } else {
        toast.error('Could not find location — try a more specific address');
      }
    } finally {
      setGeocoding(false);
    }
  };

  const handleInvoiceUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (actualCost) formData.append('actual_cost', actualCost);
      if (invoiceNum) formData.append('invoice_number', invoiceNum);
      await api.post(`/repairs/${id}/upload-invoice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Invoice PDF uploaded!');
      setInvoiceNum('');
      setActualCost('');
      load();
      setShowNextStep(true); // Ask ops what to do next
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleInvoiceUpload(file);
  };

  const handleRouteToGenerator = async () => {
    setRoutingToGenerator(true);
    try {
      const res = await api.post(`/invoice-transformer/from-repair/${id}`);
      toast.success('Linked to Invoice Generator!');
      navigate(`/ops/invoice-transformer/supplier/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link to Invoice Generator');
      setRoutingToGenerator(false);
    }
  };

  const handleAddLineItem = async (e) => {
    e.preventDefault();
    if (!newItem.category) return;
    setAddingItem(true);
    try {
      await api.post(`/repairs/${id}/line-items`, {
        ...newItem,
        qty: newItem.qty ? Number(newItem.qty) : undefined,
        amount: newItem.amount ? Number(newItem.amount) : undefined,
      });
      toast.success('Line item added');
      setNewItem(EMPTY_ITEM);
      setShowAddItem(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleDeleteLineItem = async (itemId) => {
    try {
      await api.delete(`/repairs/${id}/line-items/${itemId}`);
      toast.success('Item removed');
      load();
    } catch {
      toast.error('Failed to remove item');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/repairs/${id}`);
      setRepair(res.data);
      setEditForm({
        status: res.data.status, priority: res.data.priority, category: res.data.category,
        vendor_name: res.data.vendor_name || '', assigned_vendor_id: res.data.assigned_vendor_id || '',
        estimate_amount: res.data.estimate_amount || '',
        actual_cost: res.data.actual_cost || '', payment_state: res.data.payment_state,
        mileage_at_repair: res.data.mileage_at_repair || '', downtime_hours: res.data.downtime_hours || '',
        location: res.data.location, issue_description: res.data.issue_description,
      });
      // Load linked customer invoices
      try {
        const invRes = await api.get(`/invoice-transformer/customer-invoices?repair_id=${id}&limit=20`);
        setLinkedInvoices(invRes.data.items || []);
      } catch { setLinkedInvoices([]); }
    } catch (e) {
      toast.error('Failed to load repair');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (quickUpdate) => {
    setUpdating(true);
    try {
      let payload = quickUpdate || editForm;
      if (!quickUpdate) {
        // Normalize the edit form: empty strings on number fields are NaN to
        // Pydantic and trigger a 422. Convert "" → null so the backend treats
        // them as "no change to existing value" or "explicit clear".
        const NUMERIC = ['estimate_amount', 'actual_cost', 'mileage_at_repair', 'downtime_hours'];
        payload = Object.fromEntries(
          Object.entries(editForm).map(([k, v]) => [
            k,
            NUMERIC.includes(k) && (v === '' || v === undefined) ? null : v,
          ])
        );
      }
      await api.put(`/repairs/${id}`, payload);
      toast.success('Updated!');
      setEditMode(false);
      load();
    } catch (e) {
      // FastAPI sends Pydantic 422 errors as `detail: [{loc, msg, ...}]`. Format
      // them so the toast doesn't choke on an array (which previously caused
      // the entire page to crash into the error boundary).
      let msg = 'Update failed';
      const detail = e.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail.map(x => {
          const field = Array.isArray(x.loc) ? x.loc.slice(-1)[0] : '';
          return field ? `${field}: ${x.msg}` : x.msg;
        }).join(' · ');
      }
      toast.error(msg);
    } finally {
      setUpdating(false);
    }
  };

  // When ops clicks "Repair Shop Locating", prompt for the vendor before flipping
  // the status. Helps remind the dispatcher to actually log which shop they're
  // calling, so the vendor portal lights up for that shop (or just records the
  // free-text name for unlisted shops they're booking by phone).
  const handleShopLocatingClick = () => {
    setShopLocatingVendorId(repair.assigned_vendor_id || '');
    setShopLocatingVendorName(repair.vendor_name || '');
    setShopLocatingPrompt(true);
  };

  const confirmShopLocating = async (skipVendor = false) => {
    setUpdating(true);
    try {
      const payload = { status: 'estimate_requested' };
      if (!skipVendor) {
        if (shopLocatingVendorId) {
          payload.assigned_vendor_id = shopLocatingVendorId;
          const v = vendors.find(x => x.id === shopLocatingVendorId);
          if (v) payload.vendor_name = v.name;
        } else if (shopLocatingVendorName) {
          payload.vendor_name = shopLocatingVendorName;
          payload.assigned_vendor_id = null;
        }
      }
      await api.put(`/repairs/${id}`, payload);
      toast.success(skipVendor ? 'Status updated' : 'Vendor assigned & status updated');
      setShopLocatingPrompt(false);
      load();
    } catch (e) {
      let msg = 'Update failed';
      const detail = e.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail.map(x => x.msg).join(' · ');
      }
      toast.error(msg);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try {
      await api.post(`/repairs/${id}/timeline`, { note: noteText });
      setNoteText('');
      toast.success('Note added');
      load();
    } catch (e) {
      toast.error('Failed to add note');
    }
  };

  const fmt = (n) => n ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={28} /></div>;
  if (!repair) return <div className="p-6 text-[#8B9AAB]">Repair not found</div>;

  const truck = repair.truck;
  const customer = repair.customer;
  const timeline = [...(repair.timeline || [])].reverse();

  return (
    <>
    <div className="p-6 space-y-6 animate-fade-in" data-testid="repair-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/ops/repairs')} className="text-[#8B9AAB] hover:text-[#3B82F6] transition-colors mt-1">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-[#E8EAED] font-['Manrope'] font-bold text-xl">{repair.issue_description}</h1>
            <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['IBM_Plex_Sans'] ${STATUS_COLORS[repair.status]}`} data-testid="repair-status-badge">
              {formatStatus(repair.status)}
            </span>
            <span className={`text-xs font-bold uppercase font-['IBM_Plex_Sans'] ${PRIORITY_COLORS[repair.priority]}`}>{repair.priority}</span>
          </div>
          <p className="text-[#8B9AAB] font-['IBM_Plex_Sans'] text-sm mt-1">
            {customer?.company_name} · {truck ? `${truck.unit_number} — ${truck.year} ${truck.make} ${truck.model}` : 'N/A'} · {repair.location}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — Detail */}
        <div className="lg:col-span-2 space-y-5">
          {/* Info Card */}
          <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans']">Repair Details</p>
              <button onClick={() => setEditMode(!editMode)} className="text-xs text-[#3B82F6] hover:underline font-['IBM_Plex_Sans']">{editMode ? 'Cancel' : 'Edit'}</button>
            </div>
            {editMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {[['Status','status','select',STATUSES],['Priority','priority','select',['emergency','high','medium','low']],['Category','category','select',CATS],['Payment State','payment_state','select',['unpaid','paid','overdue']]].map(([label,key,type,opts]) => (
                    <div key={key}>
                      <label className="block text-[10px] uppercase tracking-widest text-[#8B9AAB] font-['IBM_Plex_Sans'] mb-1">{label}</label>
                      <select value={editForm[key]} onChange={e => setEditForm(p => ({...p,[key]:e.target.value}))}
                        className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-[#E8EAED] font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                        {opts.map(o => <option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
                      </select>
                    </div>
                  ))}
                  {[['Estimate ($)','estimate_amount'],['Actual Cost ($)','actual_cost'],['Mileage','mileage_at_repair'],['Downtime (hrs)','downtime_hours']].map(([label,key]) => (
                    <div key={key}>
                      <label className="block text-[10px] uppercase tracking-widest text-[#8B9AAB] font-['IBM_Plex_Sans'] mb-1">{label}</label>
                      <input type="number" value={editForm[key]} onChange={e => setEditForm(p => ({...p,[key]:e.target.value}))}
                        className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-[#E8EAED] font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#8B9AAB] font-['IBM_Plex_Sans'] mb-1">Assigned Vendor / Shop</label>
                  <select
                    value={editForm.assigned_vendor_id || ''}
                    onChange={e => {
                      const vid = e.target.value;
                      const v = vendors.find(x => x.id === vid);
                      setEditForm(p => ({
                        ...p,
                        assigned_vendor_id: vid || null,
                        vendor_name: v ? v.name : p.vendor_name,
                      }));
                    }}
                    data-testid="assign-vendor-select"
                    className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-[#E8EAED] font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                    <option value="">— None (manual / external) —</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.name}{v.city ? ` — ${v.city}, ${v.us_state || ''}` : ''}
                      </option>
                    ))}
                  </select>
                  <input type="text" value={editForm.vendor_name} onChange={e => setEditForm(p => ({...p,vendor_name:e.target.value}))}
                    placeholder="Or type a shop name for an unlisted vendor"
                    className="w-full h-9 px-3 mt-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-[#E8EAED] font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
                  {editForm.assigned_vendor_id && (
                    <p className="text-[10px] mt-1.5 font-['JetBrains_Mono']" style={{ color: 'var(--success)' }}>
                      ✓ Vendor user(s) linked to this shop will see this work order in their portal.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#8B9AAB] font-['IBM_Plex_Sans'] mb-1">Update Note (added to timeline)</label>
                  <input type="text" value={editForm.update_note || ''} onChange={e => setEditForm(p => ({...p,update_note:e.target.value}))}
                    className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-[#E8EAED] font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" placeholder="Optional note..." />
                </div>
                <button onClick={() => handleUpdate()} disabled={updating} data-testid="save-repair-btn"
                  className="px-4 h-9 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#3B82F6]/90 transition-colors disabled:opacity-50 flex items-center gap-2">
                  {updating && <Loader2 size={13} className="animate-spin" />} Save Changes
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {[
                  ['Category', repair.category], ['Vendor', repair.vendor_name || '—'],
                  ['Estimate', fmt(repair.estimate_amount)], ['Actual Cost', fmt(repair.actual_cost)],
                  ['Payment', repair.payment_state], ['Mileage', repair.mileage_at_repair ? `${repair.mileage_at_repair?.toLocaleString()} mi` : '—'],
                  ['Downtime', repair.downtime_hours ? `${repair.downtime_hours}h` : '—'], ['Location', repair.location],
                  ['Assigned To', repair.assigned_operator_name || '—'], ['Created', repair.created_at ? new Date(repair.created_at).toLocaleDateString() : '—'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans']">{label}</p>
                    <p className={`text-sm font-['IBM_Plex_Sans'] mt-0.5 ${label === 'Payment' ? PAYMENT_COLORS[value] || 'text-[#E8EAED]' : 'text-[#E8EAED]'}`}>{value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Line Items ── */}
          <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['JetBrains_Mono'] mb-0.5">// CLOSEOUT</p>
                <p className="text-white font-['Manrope'] font-bold text-sm">Repair Line Items</p>
              </div>
              {!showAddItem && (
                <button onClick={() => setShowAddItem(true)} data-testid="add-line-item-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2D3F5E] text-[#8B9AAB] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
                  <Plus size={12} /> Add Item
                </button>
              )}
            </div>

            {/* Existing items */}
            {(repair.line_items || []).length > 0 && (
              <div className="mb-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#2D3F5E]">
                      {['CATEGORY','SUBTYPE','POSITION','DESCRIPTION','QTY','AMOUNT',''].map(h => (
                        <th key={h} className="text-left px-2 py-2 text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#5C6A7A]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {repair.line_items.map(item => (
                      <tr key={item.id} className="border-b border-[#2D3F5E]/30">
                        <td className="px-2 py-2 text-[#3B82F6] font-['JetBrains_Mono'] font-bold whitespace-nowrap">{getCategoryLabel(item.category)}</td>
                        <td className="px-2 py-2 text-[#B8C0CC] font-['IBM_Plex_Sans']">{formatSubtype(item.subtype)}</td>
                        <td className="px-2 py-2">
                          {item.position_code && item.position_code !== 'NONE' ? (
                            <span className="px-1.5 py-0.5 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded text-[9px] font-bold font-['JetBrains_Mono'] text-[#3B82F6]">
                              {item.position_code}
                            </span>
                          ) : <span className="text-[#2D3F5E]">—</span>}
                        </td>
                        <td className="px-2 py-2 text-[#5C6A7A] max-w-[120px] truncate">{item.description || '—'}</td>
                        <td className="px-2 py-2 text-[#B8C0CC] font-['JetBrains_Mono']">{item.qty || '—'}</td>
                        <td className="px-2 py-2 text-[#B8C0CC] font-['JetBrains_Mono']">{item.amount ? `$${Number(item.amount).toLocaleString()}` : '—'}</td>
                        <td className="px-2 py-2">
                          <button onClick={() => handleDeleteLineItem(item.id)} className="text-[#2D3F5E] hover:text-red-400 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {repair.line_items.some(i => i.amount) && (
                      <tr className="border-t border-[#2D3F5E]">
                        <td colSpan={5} className="px-2 py-2 text-right text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Total</td>
                        <td className="px-2 py-2 font-['JetBrains_Mono'] font-bold text-[#E8EAED]">
                          ${repair.line_items.reduce((a, i) => a + (Number(i.amount) || 0), 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                        </td>
                        <td />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {(repair.line_items || []).length === 0 && !showAddItem && (
              <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-xs mb-3">No line items yet. Add what was repaired/replaced.</p>
            )}

            {/* Add item form */}
            {showAddItem && (
              <form onSubmit={handleAddLineItem} className="border border-[#2D3F5E] rounded-sm p-4 space-y-3 bg-[#0F172A]">
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#5C6A7A]">New Line Item</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Category *</label>
                    <select value={newItem.category}
                      onChange={e => setNewItem(p => ({ ...p, category: e.target.value, subtype: '', position_code: 'NONE' }))}
                      required className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
                      <option value="">Select...</option>
                      {CATEGORY_LIST.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Subtype</label>
                    <select value={newItem.subtype}
                      onChange={e => setNewItem(p => ({ ...p, subtype: e.target.value }))}
                      className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
                      <option value="">Select...</option>
                      {getSubtypes(newItem.category).map(s => <option key={s} value={s}>{formatSubtype(s)}</option>)}
                    </select>
                  </div>
                </div>

                {requiresPosition(newItem.category, newItem.subtype) && (
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#3B82F6] mb-2">
                      Position * (required for {getCategoryLabel(newItem.category)})
                    </label>
                    <PositionPicker value={newItem.position_code} onChange={code => setNewItem(p => ({ ...p, position_code: code }))} />
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Description</label>
                    <input type="text" value={newItem.description} onChange={e => setNewItem(p => ({ ...p, description: e.target.value }))}
                      placeholder="Optional notes..." className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Qty</label>
                    <input type="number" step="0.1" value={newItem.qty} onChange={e => setNewItem(p => ({ ...p, qty: e.target.value }))}
                      className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Amount ($)</label>
                    <input type="number" step="0.01" value={newItem.amount} onChange={e => setNewItem(p => ({ ...p, amount: e.target.value }))}
                      className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={addingItem || !newItem.category} data-testid="confirm-add-item"
                    className="px-4 h-8 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-xs uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50 flex items-center gap-1.5">
                    {addingItem ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Add
                  </button>
                  <button type="button" onClick={() => { setShowAddItem(false); setNewItem(EMPTY_ITEM); }}
                    className="px-4 h-8 border border-[#2D3F5E] text-[#5C6A7A] rounded-sm text-xs hover:border-red-800/50 hover:text-red-400 transition-colors font-['JetBrains_Mono']">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* ── Closeout Checklist ── */}
          {repair.status !== 'completed' && repair.status !== 'cancelled' && (
            <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-5">
              <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['JetBrains_Mono'] mb-3">// CLOSEOUT CHECKLIST</p>
              {(() => {
                const issues = getCloseoutIssues(repair);
                const ready = issues.length === 0;
                return (
                  <div className="space-y-2">
                    {[
                      { key: 'mileage', label: 'Mileage at repair entered', ok: !!repair.mileage_at_repair },
                      { key: 'invoice', label: 'Invoice PDF uploaded', ok: !!(repair.invoice_uploaded || repair.invoice_pdf_url) },
                      { key: 'line_items', label: `Line items added (${repair.line_items?.length || 0})`, ok: !!(repair.line_items?.length) },
                      { key: 'position', label: 'Position entered for required categories', ok: !issues.find(i => i.key === 'position') },
                    ].map(({ key, label, ok }) => (
                      <div key={key} className="flex items-center gap-2.5">
                        {ok ? <CheckCircle2 size={14} className="text-[#57EB31] flex-shrink-0" /> : <XCircle size={14} className="text-red-400 flex-shrink-0" />}
                        <span className={`text-xs font-['IBM_Plex_Sans'] ${ok ? 'text-[#57EB31]' : 'text-red-400'}`}>{label}</span>
                      </div>
                    ))}
                    {ready ? (
                      <button onClick={() => handleUpdate({ status: 'completed' })} disabled={updating}
                        className="w-full mt-3 h-10 bg-[#57EB31] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#57EB31]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="complete-repair-btn">
                        {updating ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Mark as Completed
                      </button>
                    ) : (
                      <p className="text-[10px] text-[#5C6A7A] font-['JetBrains_Mono'] mt-2">
                        Complete checklist above before closing repair
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Add Note */}
          <form onSubmit={handleAddNote} className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-4">
            <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans'] mb-3">Add Timeline Note</p>
            <div className="flex gap-3">
              <input value={noteText} onChange={e => setNoteText(e.target.value)}
                className="flex-1 h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-[#E8EAED] font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors"
                placeholder="Add a note or update..." data-testid="timeline-note-input" />
              <button type="submit" className="px-4 h-9 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-xs uppercase tracking-wide hover:bg-[#3B82F6]/90 transition-colors" data-testid="add-note-btn">
                Add
              </button>
            </div>
          </form>

          {/* Timeline */}
          <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-5">
            <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans'] mb-4">Event Timeline</p>
            <div className="space-y-4" data-testid="repair-timeline">
              {timeline.length === 0 ? (
                <p className="text-[#8B9AAB] text-sm font-['IBM_Plex_Sans']">No events yet</p>
              ) : timeline.map((ev) => (
                <div key={ev.id} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#0F172A] border border-[#2D3F5E] flex items-center justify-center">
                    <Clock size={12} className="text-[#8B9AAB]" />
                  </div>
                  <div className="flex-1 pb-4 border-b border-[#2D3F5E]/40 last:border-0">
                    <p className="text-sm text-[#E8EAED] font-['IBM_Plex_Sans']">{ev.description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans']">{ev.created_by_name}</span>
                      <span className="text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ''}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          {truck && (
            <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans'] mb-3">Unit Info</p>
              <p className="font-['JetBrains_Mono'] text-lg font-bold text-[#3B82F6] mb-1">{truck.unit_number}</p>
              <p className="text-sm text-[#E8EAED] font-['IBM_Plex_Sans']">{truck.year} {truck.make} {truck.model}</p>
              <p className="text-xs text-[#8B9AAB] font-['JetBrains_Mono'] mt-1">{truck.current_mileage?.toLocaleString()} mi</p>
              {truck.plate && <p className="text-xs text-[#5C6A7A] mt-1">{truck.plate}</p>}
            </div>
          )}

          {/* Linked Customer Invoices */}
          <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-4" data-testid="linked-invoices-section">
            <div className="flex items-center gap-2 mb-3">
              <Receipt size={14} className="text-[#3B82F6]" />
              <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans']">Linked Invoices</p>
            </div>
            {linkedInvoices.length === 0 ? (
              <p className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] py-2">No invoices generated for this repair yet</p>
            ) : (
              <div className="space-y-2">
                {linkedInvoices.map(inv => (
                  <div key={inv.id} className="p-2.5 rounded-sm border border-[#2D3F5E] bg-[#0F172A] hover:border-[#3B82F6]/40 transition-colors"
                    data-testid={`linked-invoice-${inv.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-['JetBrains_Mono'] font-bold text-[#3B82F6]">{inv.invoice_number}</span>
                      <span className={`text-[9px] font-['JetBrains_Mono'] uppercase px-1.5 py-0.5 rounded-sm border ${inv.payment_status === 'paid' ? 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/30' : 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30'}`}>
                        {inv.payment_status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-[#B8C0CC] font-['IBM_Plex_Sans']">{inv.invoice_date}</span>
                      <span className="text-sm font-['JetBrains_Mono'] font-bold text-white">{fmt(inv.totals?.total_due)}</span>
                    </div>
                    {inv.pdf_url && (
                      <a href={`${BACKEND_URL}${inv.pdf_url}?token=${localStorage.getItem('mm_token')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 mt-2 text-[10px] text-[#57EB31] hover:underline font-['JetBrains_Mono']"
                        data-testid={`download-linked-invoice-${inv.id}`}>
                        <Download size={10} /> Download PDF
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {customer && (
            <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans'] mb-3">Client</p>
              <p className="text-sm font-bold text-[#E8EAED] font-['IBM_Plex_Sans'] mb-1">{customer.company_name}</p>
              <p className="text-xs text-[#8B9AAB] font-['IBM_Plex_Sans']">{customer.contact_email}</p>
              {customer.contact_phone && <p className="text-xs text-[#8B9AAB] font-['IBM_Plex_Sans'] mt-0.5">{customer.contact_phone}</p>}
              <p className="text-[9px] text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-2">Approval threshold: ${customer.approval_threshold?.toLocaleString()}</p>
            </div>
          )}

          {/* Map */}
          <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans'] flex items-center gap-1.5"><MapPin size={10} /> Repair Location</p>
              {!repair.lat && (
                <button onClick={handleGeocode} disabled={geocoding} className="text-[10px] text-[#3B82F6] hover:underline font-['JetBrains_Mono'] flex items-center gap-1 disabled:opacity-50">
                  {geocoding ? <Loader2 size={10} className="animate-spin" /> : null} Pin on Map
                </button>
              )}
            </div>
            <p className="text-xs text-[#B8C0CC] font-['IBM_Plex_Sans'] mb-3">{repair.location}</p>
            <MapView lat={repair.lat} lng={repair.lng} label={`${repair.truck?.unit_number || ''} — ${repair.location}`} priority={repair.priority} height="200px" />
          </div>

          {/* Best Option Finder */}
          <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-4">
            <BestOptionFinder
              repairId={id}
              defaultCategory={repair.category}
              defaultState={repair.location?.split(',')?.pop()?.trim()?.toUpperCase()?.slice(-2) || ''}
              onAssign={(vendorName, vendorId) => {
                handleUpdate({ vendor_name: vendorName, update_note: `Vendor assigned: ${vendorName} (via Best Option Finder)` });
              }}
            />
          </div>

          {/* ── Final Invoice PDF Upload ── */}
          <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans']">Final Invoice PDF</p>
              {repair.invoice_pdf_url && (
                <span className="flex items-center gap-1 text-[9px] font-['JetBrains_Mono'] text-[#57EB31]">
                  <FileCheck size={10} /> Uploaded
                </span>
              )}
            </div>

            {repair.invoice_pdf_url ? (
              <div className="space-y-2">
                <a href={`${BACKEND_URL}${repair.invoice_pdf_url}?token=${localStorage.getItem('mm_token')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-[#57EB31]/10 border border-[#57EB31]/30 text-[#57EB31] rounded-sm text-xs font-bold font-['Manrope'] uppercase tracking-wide hover:bg-[#57EB31]/20 transition-colors"
                  data-testid="download-invoice-pdf">
                  <Download size={13} /> Download Invoice PDF
                </a>
                <button onClick={() => fileInputRef.current?.click()}
                  className="w-full text-[10px] text-[#5C6A7A] hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono'] py-1">
                  Replace PDF
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Invoice # (opt)</label>
                    <input type="text" value={invoiceNum} onChange={e => setInvoiceNum(e.target.value)}
                      placeholder="INV-2024-001"
                      className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Final Cost ($)</label>
                    <input type="number" step="0.01" value={actualCost} onChange={e => setActualCost(e.target.value)}
                      placeholder="2400.00"
                      className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                </div>
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-sm p-5 text-center cursor-pointer transition-all ${dragOver ? 'border-[#3B82F6] bg-[#3B82F6]/5' : 'border-[#2D3F5E] hover:border-[#3B82F6]/50'}`}
                  data-testid="pdf-drop-zone">
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 size={20} className="text-[#3B82F6] animate-spin" />
                      <p className="text-xs text-[#8B9AAB] font-['IBM_Plex_Sans']">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload size={20} className={`mx-auto mb-2 ${dragOver ? 'text-[#3B82F6]' : 'text-[#5C6A7A]'}`} />
                      <p className="text-xs font-['IBM_Plex_Sans'] text-[#8B9AAB]">Drop shop invoice PDF here</p>
                      <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-1">or click to browse</p>
                    </>
                  )}
                </div>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept=".pdf" className="hidden"
              onChange={e => handleInvoiceUpload(e.target.files[0])} data-testid="pdf-file-input" />
          </div>

          <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm p-4 space-y-3">
            <p className="text-[9px] uppercase tracking-widest text-[#5C6A7A] font-['IBM_Plex_Sans']">Quick Status Update</p>
            {['open','estimate_requested','estimate_received','approved','repair_in_progress','completed'].map(s => (
              <button key={s}
                onClick={() => s === 'estimate_requested' ? handleShopLocatingClick() : handleUpdate({ status: s })}
                disabled={repair.status === s || updating}
                className={`w-full py-1.5 px-3 rounded-sm text-xs font-bold uppercase tracking-wide font-['IBM_Plex_Sans'] border transition-colors disabled:opacity-40 ${STATUS_COLORS[s]}`}
                data-testid={`quick-status-${s}`}>
                → {STATUS_LABELS[s]}
              </button>
            ))}
            {/* Final "Create Millennials Invoice" CTA — only when repair is COMPLETED */}
            {repair.status === 'completed' && (
              <button
                onClick={() => navigate(`/ops/invoice-transformer?from_repair=${repair.id}`)}
                className="w-full py-2 px-3 rounded-sm text-xs font-bold uppercase tracking-wide font-['IBM_Plex_Sans'] border-2 border-cyan-500 bg-cyan-900/30 text-cyan-300 hover:bg-cyan-900/50 transition-colors flex items-center justify-center gap-2"
                data-testid="create-millennials-invoice-btn">
                <Receipt size={14} /> Create Millennials Invoice →
              </button>
            )}
            {/* Show linked invoice if already created */}
            {repair.status === 'invoice_created' && repair.invoice_number && (
              <div className="p-2.5 rounded-sm border border-cyan-700/50 bg-cyan-900/20 text-xs space-y-1" data-testid="linked-invoice-info">
                <p className="text-cyan-300 font-bold uppercase tracking-wide text-[9px]">Linked Invoice</p>
                <p className="font-mono text-cyan-200">{repair.invoice_number}</p>
                <button onClick={() => navigate(`/ops/invoices?search=${repair.invoice_number}`)}
                  className="text-[10px] underline text-cyan-400 hover:text-cyan-300">
                  View invoice →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

      <Dialog open={showNextStep} onOpenChange={setShowNextStep}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-['Manrope']">Invoice PDF Uploaded</DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-4">
            <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm">
              The shop invoice PDF is now attached to this repair. What would you like to do with it?
            </p>

            {/* Option A — Final, send to customer as-is */}
            <button
              onClick={() => { setShowNextStep(false); toast.success('Invoice attached — customer can now download it.'); }}
              className="w-full p-4 border border-[#57EB31]/40 bg-[#57EB31]/5 rounded-sm text-left hover:border-[#57EB31]/70 hover:bg-[#57EB31]/10 transition-all group"
              data-testid="invoice-final-btn">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-[#57EB31] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-['Manrope'] font-bold text-sm group-hover:text-[#57EB31] transition-colors">
                    This is the final invoice
                  </p>
                  <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-xs mt-1">
                    Attach as-is to the customer's portal. No markup needed — they download this PDF directly.
                  </p>
                </div>
              </div>
            </button>

            {/* Option B — Route to Invoice Generator */}
            <button
              onClick={handleRouteToGenerator}
              disabled={routingToGenerator}
              className="w-full p-4 border border-[#3B82F6]/40 bg-[#3B82F6]/5 rounded-sm text-left hover:border-[#3B82F6]/70 hover:bg-[#3B82F6]/10 transition-all group disabled:opacity-50"
              data-testid="invoice-to-generator-btn">
              <div className="flex items-start gap-3">
                {routingToGenerator
                  ? <Loader2 size={20} className="text-[#3B82F6] mt-0.5 flex-shrink-0 animate-spin" />
                  : <ChevronRight size={20} className="text-[#3B82F6] mt-0.5 flex-shrink-0" />
                }
                <div>
                  <p className="text-white font-['Manrope'] font-bold text-sm group-hover:text-[#3B82F6] transition-colors">
                    Send to Invoice Generator
                  </p>
                  <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-xs mt-1">
                    Apply markup %, service fee, and generate a branded customer invoice with your pricing.
                    The shop costs stay hidden.
                  </p>
                </div>
              </div>
            </button>

            <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] text-center">
              You can always change this later from the Invoice Generator.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* SHOP LOCATING — vendor assignment prompt */}
      <Dialog open={shopLocatingPrompt} onOpenChange={setShopLocatingPrompt}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-['Manrope'] flex items-center gap-2">
              <Store size={18} /> Which shop is handling this?
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-4">
            <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
              Optional — pick a shop from your vendor list so they can see this work order in their portal,
              type a name for an unlisted shop you booked by phone, or skip if you haven't decided yet.
            </p>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">From your vendor list</label>
              <select
                value={shopLocatingVendorId}
                onChange={e => {
                  const vid = e.target.value;
                  setShopLocatingVendorId(vid);
                  const v = vendors.find(x => x.id === vid);
                  if (v) setShopLocatingVendorName(v.name);
                }}
                data-testid="shop-locating-vendor-select"
                className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white text-sm focus:outline-none focus:border-[#3B82F6]">
                <option value="">— Select a vendor —</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.name}{v.city ? ` — ${v.city}, ${v.us_state || ''}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Or type a shop name (unlisted / phone-booked)</label>
              <input
                type="text"
                value={shopLocatingVendorName}
                onChange={e => { setShopLocatingVendorName(e.target.value); if (e.target.value) setShopLocatingVendorId(''); }}
                placeholder="e.g. Acme Brake & Tire"
                data-testid="shop-locating-vendor-name"
                className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white text-sm focus:outline-none focus:border-[#3B82F6]" />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => confirmShopLocating(true)}
                disabled={updating}
                data-testid="shop-locating-skip"
                className="flex-1 py-2 px-3 rounded-sm text-xs font-bold uppercase tracking-wide font-['JetBrains_Mono'] border border-[#2D3F5E] text-[#8B9AAB] hover:bg-[#0A0F1A] transition-colors disabled:opacity-50">
                Skip — assign later
              </button>
              <button
                onClick={() => confirmShopLocating(false)}
                disabled={updating || (!shopLocatingVendorId && !shopLocatingVendorName)}
                data-testid="shop-locating-confirm"
                className="flex-1 py-2 px-3 rounded-sm text-xs font-bold uppercase tracking-wide font-['JetBrains_Mono'] bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors disabled:opacity-50">
                {updating ? 'Saving…' : 'Confirm & Start Locating'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
}
